import React, { useMemo } from "react";
import { styled } from "@linaria/react";
import { color, fonts, radius, stableColor } from "@ui";
import t from "../../../../../i18n";
import ColorPicker from "@ui/ColorPicker";
import Popper from "@ui/Popper";
import Tooltip from "./Tooltip";
import useToasts from "@ui/Toast/useToasts";
import type { Failure } from "../../../../../api/useSetups";
import type { Project } from "../../../../../api/useProjects";
import useMutateFailureColor, { getFailureColor } from "../../../useFailureColor";
import type { ManualFailure } from "../../../../../api/useManualFailures";
import { useManualFailures } from "../../../../../api/useManualFailures";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import { usePut } from "../../../../../api/useCall";

interface P {
  project: Project;
  failureName: string;
  failure: Failure | ManualFailure | undefined;
  symbol: React.ReactNode;
  hideFaultActions?: boolean;
  hideColorPicker?: boolean;
  renderFailureActions?: (failure: Failure | ManualFailure) => React.ReactNode;
  renderColorItemContent?: (color: string) => JSX.Element;
}

const ColorPopper = styled(Popper)`
  border-radius: 4px;
  background-color: ${color.hydrogen};
  color: ${color.carbon800};
  padding: 12px;
  min-width: 100px;
  box-shadow: 0px 7px 6px #00000029;
  cursor: auto;
  font: ${fonts.body2};
`;

const FailureMenu = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 3px;
  border-radius: ${radius}px;
  cursor: pointer;
  min-height: 12px;

  :hover {
    background-color: ${color.titanium200};
  }
`;

type LabelKey = "system" | "equipments" | "suspectedElements";
function getI18NLabel(labelKey: LabelKey): string {
  switch (labelKey) {
    case "system":
      return t("failureTooltip.system");
    case "equipments":
      return t("failureTooltip.equipments");
    case "suspectedElements":
      return t("failureTooltip.suspectedElements");
    default:
      throw new Error(labelKey satisfies never);
  }
}
const FailurePropertyKey = styled.span`
  font-weight: bold;
`;
function FailureProperty({ property, labelKey }: { property: string; labelKey: LabelKey }) {
  return (
    <div>
      <FailurePropertyKey>{getI18NLabel(labelKey)}</FailurePropertyKey> : {property}
    </div>
  );
}

const TooltipSection = styled.div`
  border-top: 1px solid;
  border-top-color: ${color.titanium200};
  padding-top: 8px;
  margin-top: 12px;
`;

function FailureTooltip({
  project,
  failureName,
  failure,
  renderFailureActions,
  symbol,
  renderColorItemContent,
  hideFaultActions = false,
  hideColorPicker = false,
}: P) {
  const canChangeColor = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const defaultColor = stableColor(failureName);
  const type = useMemo(
    () => (failure && "impacts" in failure ? "FAILURE" : "MANUAL_FAILURE"),
    [failure],
  );
  const colorFailure = getFailureColor(project, type, failureName);
  const [lastSavedColor, setLastSavedColor] = React.useState<string>(colorFailure);
  const cleanupTimeoutRef = React.useRef<number | null>(null);
  const { addToast } = useToasts();
  const mutateGlobalFailureColors = useMutateFailureColor(project);
  const updateProject = usePut("/projects/:projectId(uuid)");

  const handleColorClick = React.useCallback(
    async (color: string) => {
      const old = lastSavedColor;
      try {
        setLastSavedColor(color);
        await updateProject({
          body: project,
          routeParams: { projectId: project.id },
        });
        addToast("success", t("project.display_settings_saved"));
        mutateGlobalFailureColors(type, failureName, color);
      } catch (e) {
        setLastSavedColor(old);
        throw e;
      }
    },
    [
      failureName,
      mutateGlobalFailureColors,
      type,
      addToast,
      lastSavedColor,
      updateProject,
      project,
    ],
  );
  const handleColorMouseEnter = React.useCallback(
    (color: string) => {
      if (cleanupTimeoutRef.current !== null) {
        window.clearTimeout(cleanupTimeoutRef.current);
      }
      mutateGlobalFailureColors(type, failureName, color);
    },
    [failureName, mutateGlobalFailureColors, type],
  );
  const handleColorMouseLeave = React.useCallback(() => {
    cleanupTimeoutRef.current = window.setTimeout(() => {
      mutateGlobalFailureColors(type, failureName, lastSavedColor);
      cleanupTimeoutRef.current = null;
    }, 300);
  }, [failureName, mutateGlobalFailureColors, lastSavedColor, type]);

  const hasOtherCategoryInformation = (failure: Failure | ManualFailure) =>
    failure.equipments.length > 0 || failure.suspectedElements.length > 0 || failure.systemId;

  const actions = failure && renderFailureActions?.(failure);
  const manualFailures = useManualFailures(project.id);

  const isManualFailure = React.useMemo(
    () =>
      failure ? manualFailures.map(manualFailure => manualFailure.id).includes(failure.id) : false,
    [failure, manualFailures],
  );

  const serieName =
    failure && "displayName" in failure && failure.displayName ? failure.displayName : failureName;

  return (
    <Tooltip
      serieName={serieName}
      symbol={
        hideColorPicker ? null : canChangeColor ? (
          <ColorPopper
            click
            keepOpenOnOverlayActive
            overlay={
              <ColorPicker
                defaultColor={defaultColor}
                colorValue={lastSavedColor}
                renderColorItemContent={renderColorItemContent}
                onColorClick={handleColorClick}
                onColorEnter={handleColorMouseEnter}
                onColorLeave={handleColorMouseLeave}
              />
            }
            openDelay={1000}
            closeDelay={200}
            gap={0}
            placement="bottom-start"
            strategy="absolute"
          >
            <FailureMenu>{symbol}</FailureMenu>
          </ColorPopper>
        ) : (
          symbol
        )
      }
    >
      {failure ? (
        <div>
          {"description" in failure &&
            failure.description !== null &&
            failure.description.length > 0 && (
              <TooltipSection>{failure.description}</TooltipSection>
            )}

          {hasOtherCategoryInformation(failure) && (
            <TooltipSection>
              <>
                {failure?.systemId && (
                  <FailureProperty property={failure?.systemId} labelKey="system" />
                )}
              </>
              <>
                {failure.equipments && failure.equipments.length > 0 && (
                  <FailureProperty
                    property={failure.equipments.map(e => e.equipment.name).join(", ")}
                    labelKey="equipments"
                  />
                )}
              </>
              <>
                {failure.suspectedElements && failure.suspectedElements.length > 0 && (
                  <FailureProperty
                    property={failure.suspectedElements
                      .map(m => m.suspectedElement.name)
                      .join(", ")}
                    labelKey="suspectedElements"
                  />
                )}
              </>
            </TooltipSection>
          )}

          {isManualFailure && (
            <TooltipSection>{t("diagnosis.chart.legend.tooltip.is_manual_failure")}</TooltipSection>
          )}
          {!hideFaultActions && !isManualFailure && actions && (
            <TooltipSection>{actions}</TooltipSection>
          )}
        </div>
      ) : (
        t("failureTooltip.notInSetup")
      )}
    </Tooltip>
  );
}

export default FailureTooltip;
